import { ObjectSuperTypesEnum, SelectionTypeEnum } from "../../../enums";
import { CanvasHandler } from "../../../handlers";
import { TextFactory } from "../factories";
import { RowSeatLabelHelper } from "../helpers";

export abstract class TablePropertiesBase {
    protected canvasHandler: CanvasHandler;

    constructor(canvasHandler: CanvasHandler) {
        this.canvasHandler = canvasHandler;
    }

    public updateCategory = ({selectedItem, selectedCategory, table, seats, tableGroup} : any) => {
        selectedItem.set({category: {
            ...selectedCategory
        }});
        tableGroup.set({category: {
            ...selectedCategory
        }});
        const isTableSelection = selectedItem.selectionType === SelectionTypeEnum.byTable;
        table.set({
            fill: isTableSelection ? `${selectedCategory.color}40` : "#D1D1D1",
            stroke: isTableSelection ? selectedCategory.color : "#A1A1A1"
        })
        seats.forEach((seat: any) => {
            const shape = seat.item(0);
            seat.set({
                category: {
                    ...selectedCategory
                }
            })
            shape.set({
                fill: isTableSelection ? "#D1D1D1" : `${selectedCategory.color}40`,
                stroke: isTableSelection ? "#A1A1A1" : selectedCategory.color
            });
        });

        selectedItem.addWithUpdate();
    }

    public updateSelectionType = ({value, selectedItem, table, seats, tableGroup} : any) => {
        selectedItem.set({selectionType: value});
        tableGroup.set({selectionType: value});
        const { category } = selectedItem;
        const isTableSelection = selectedItem.selectionType === SelectionTypeEnum.byTable;
        table.set({
            fill: isTableSelection ? `${category.color}40` : "#D1D1D1",
            stroke: isTableSelection ? category.color : "#A1A1A1"
        })
        seats.forEach((seat: any) => {
            const shape = seat.item(0);
            shape.set({
                fill: isTableSelection ? "#D1D1D1" : `${category.color}40`,
                stroke: isTableSelection ? "#A1A1A1" : category.color
            });
            seat.set({selectionType: value});
        });
        selectedItem.addWithUpdate();
    }

    public updateSectionLabel = ({value, selectedItem, seats, tableGroup} : any) => {
        selectedItem.set({section: {
            label: value,
            displayedLabel: value
        }});
        tableGroup.set({
            value: `${value}${value && selectedItem.tableLabeling.label && "-"}${selectedItem.tableLabeling.label}`
        })
        seats.forEach((seat: any) => {
            this.setSeatValue(seat, selectedItem, seat.label)
            seat.set({
                sectionLabel: value
            })
        });
        selectedItem.addWithUpdate();
    }

    public updateLabelVisibility = ({value, selectedItem, tableGroup, table, tableText} : any) => {
        selectedItem.set({tableLabeling: {
            ...selectedItem.tableLabeling,
            visible: value
        }});
        const {label, displayedLabel} = selectedItem.tableLabeling;

        if (value) {
            if (!tableText) {
                tableText = TextFactory.create({
                    left: table.left,
                    top: table.top,
                    text: displayedLabel || label || "?",
                    fontSize: 16,
                    visible: true,
                });

                tableGroup.add(tableText);
            } else {
                tableText.set({ visible: true });
            }
        }
        else {
            tableText.set({
                visible: false,
            });
        }

        this.centerLabelOnTable(tableGroup, table, tableText);
        this.canvasHandler.canvas.requestRenderAll();
    }

    public updateTableLabel = ({value, selectedItem, tableGroup, table, tableText, seats} : any) => {
        selectedItem.set({tableLabeling: {
            ...selectedItem.tableLabeling,
            label: value
        }});
        tableGroup.set({
            value: `${selectedItem.section.label}${selectedItem.section.label && value && "-"}${value}`
        })

        if (tableText) {
            tableText.set({
                text:
                    selectedItem.tableLabeling?.displayedLabel ||
                    value ||
                    "?",
            });
            this.centerLabelOnTable(tableGroup, table, tableText);
        }
        seats.forEach((seat: any) => {
            this.setSeatValue(seat, selectedItem, seat.label)
            seat.set({
                tableLabel: value
            })
        });
        this.canvasHandler.canvas.requestRenderAll();
    }

    public updateSeatLabelingDistribution = ({value, selectedItem, table, seats} : any) => {
        selectedItem.set({seatLabeling: {
            ...selectedItem.seatLabeling,
            key: value
        }});

        this.setDistribution(selectedItem, seats, table);
        table.set({
            seatLabeling:{
                ...selectedItem.seatLabeling
            }
        })
        selectedItem.addWithUpdate();
    }

    public updateSeatLabelingStartsAt = ({value, selectedItem, table, seats} : any) => {
        selectedItem.set({seatLabeling: {
            ...selectedItem.seatLabeling,
            startsAt: value
        }});

        this.setDistribution(selectedItem, seats, table);

        selectedItem.addWithUpdate();
    }

    public updateTableDisplayedLabel = ({value, selectedItem, tableGroup, table, tableText} : any) => {
        selectedItem.set({tableLabeling: {
            ...selectedItem.tableLabeling,
            displayedLabel: value
        }});

        if (tableText) {
            tableText.set({
                text:
                    value ||
                    selectedItem.tableLabeling?.label ||
                    "?",
            });
            this.centerLabelOnTable(tableGroup, table, tableText);
        }
        this.canvasHandler.canvas.requestRenderAll();
    }

    /**
     * Centra el texto de la etiqueta sobre la figura (Round/Rect) dentro del
     * tableGroup interno.
     *
     * La figura y el texto son hermanos dentro del mismo tableGroup y ambos
     * usan originX/originY "center", así que sus left/top son offsets desde el
     * centro del grupo interno. Basta con copiar el offset de la figura al
     * texto para que el texto quede centrado sobre ella.
     *
     * IMPORTANTE: no llamamos a _calcBounds()/_updateObjectsCoords() ni a
     * addWithUpdate() sobre el grupo. Esos recálculos asumen angle = 0 y, si la
     * mesa está rotada, mueven/deforman la figura (el problema del "descuadre").
     * Como el texto es más pequeño que la figura y queda centrado en ella, los
     * límites del grupo no cambian, por lo que no hace falta recalcularlos.
     */
    private centerLabelOnTable = (tableGroup: any, table: any, tableText: any) => {
        if (!tableGroup || !table || !tableText) return;

        tableText.set({
            originX: "center",
            originY: "center",
            left: table.left,
            top: table.top,
        });
        tableText.setCoords();
        tableGroup.setCoords();
    }

    public setDistribution = (selectedItem: any, seats: any[], table: any) => {
        const orderedSeats = this.getOrderedTableSeats({
            seats,
            table,
        });
        RowSeatLabelHelper.applySeatDistribution({
            selectedItem,
            seats: orderedSeats,
            distributionKey: selectedItem.seatLabeling.key,
            startsAt: selectedItem.seatLabeling.startsAt,
            setSeatValue: this.setSeatValue,
            distributionObject: 'seatLabeling'
        });
    }

    protected abstract getOrderedTableSeats(params: {
        seats: any[];
        table: any;
    }): any[];

    public setSeatValue = (seat: any, tableData: any, seatlabel: string) => {
        const {section:{label: sectionLabel}, tableLabeling: {label}} = tableData;
        seat.set({
            value: `${sectionLabel}${sectionLabel && label && "-"}${label}${(label || sectionLabel) && seatlabel &&"-"}${seatlabel}`,
            isLabeled: label != "" && seatlabel != "",
        });
    };

    public getObjectCanvasCenter = (obj: any) => {
        const center = obj.getCenterPoint();

        return {
            x: center.x,
            y: center.y,
        };
    };

    public removeSeats = (selectedItem: any) => {
        const seats = selectedItem
            .getObjects()
            .filter((obj: any) => obj.superType === ObjectSuperTypesEnum.Seat || obj.superType === ObjectSuperTypesEnum.EmptySpace);

        seats.forEach((seat: any) => {
            selectedItem.remove(seat);
        });
    }
}